/**
 * wizardQuerySync.js
 * URL param sync for the Mortgage Smart Wizard.
 * Language-agnostic keys. Extends existing querySync pattern.
 */
import { useEffect, useRef, useCallback } from 'react';

const PARAM_MAP = {
  flow: 'flow',
  province: 'prov',
  price: 'price',
  downPayment: 'dp',
  status: 'status',
  credit: 'credit',
  income: 'income',
  offerAccepted: 'offer',
  balance: 'bal',
  homeValue: 'hval',
  refiGoal: 'goal',
  dpMode: 'dpm',
};

const REVERSE_MAP = Object.fromEntries(
  Object.entries(PARAM_MAP).map(([k, v]) => [v, k]),
);

const NUMERIC_FIELDS = new Set(['price', 'downPayment', 'income', 'balance', 'homeValue']);
const BOOL_FIELDS = new Set(['offerAccepted']);
const LS_KEY = 'fm_mortgage_wizard_v1';

export function encodeWizardParams(answers) {
  const params = new URLSearchParams();
  for (const [field, key] of Object.entries(PARAM_MAP)) {
    const val = answers[field];
    if (val === undefined || val === null || val === '' || val === false || val === 0) continue;
    if (BOOL_FIELDS.has(field)) {
      params.set(key, val ? 'yes' : 'no');
    } else {
      params.set(key, String(val));
    }
  }
  return params.toString();
}

export function decodeWizardParams(searchString) {
  const params = new URLSearchParams(searchString);
  const result = {};
  for (const [key, val] of params.entries()) {
    const field = REVERSE_MAP[key];
    if (!field) continue;
    if (BOOL_FIELDS.has(field)) {
      result[field] = val === 'yes';
    } else if (NUMERIC_FIELDS.has(field)) {
      const num = Number(val);
      if (!isNaN(num) && num > 0) result[field] = num;
    } else {
      result[field] = val;
    }
  }
  return result;
}

export function saveToLocalStorage(answers) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(answers));
  } catch { /* quota exceeded — ignore */ }
}

export function loadFromLocalStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearWizardStorage() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  // Strip query params from URL
  window.history.replaceState(null, '', window.location.pathname);
}

/**
 * Hook: sync wizard answers ↔ URL + localStorage
 */
export function useWizardQuerySync(answers, setAnswers, phase) {
  const debounceRef = useRef(null);
  const initializedRef = useRef(false);

  // On mount: hydrate from URL params → localStorage → defaults
  useEffect(() => {
    const search = window.location.search;
    if (search) {
      const parsed = decodeWizardParams(search);
      if (Object.keys(parsed).length > 0) {
        setAnswers((prev) => ({ ...prev, ...parsed }));
        initializedRef.current = true;
        return;
      }
    }
    const saved = loadFromLocalStorage();
    if (saved && typeof saved === 'object') {
      setAnswers((prev) => ({ ...prev, ...saved }));
    }
    initializedRef.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On answers change: update URL + localStorage (debounced)
  const syncToUrl = useCallback(
    (currentAnswers) => {
      if (!initializedRef.current) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const encoded = encodeWizardParams(currentAnswers);
        const newUrl = encoded
          ? `${window.location.pathname}?${encoded}`
          : window.location.pathname;
        window.history.replaceState(null, '', newUrl);
        saveToLocalStorage(currentAnswers);
      }, 300);
    },
    [],
  );

  useEffect(() => {
    if (phase !== 'entry') {
      syncToUrl(answers);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [answers, phase, syncToUrl]);
}
